import * as React from 'react';
import { Chip } from '@mui/material';
import {
  CheckCircle as PaidIcon,
  NotificationsActive as ReminderIcon,
  Gavel as CollectionIcon,
  Visibility as OpenIcon,
  PieChart as PartialIcon,
  Block as CreditLossIcon,
  HourglassEmpty as PendingSyncIcon,
  WarningAmber as NotTrackedIcon,
  RemoveCircleOutline as CancelledIcon,
  ReportProblem as ConflictIcon,
} from '@mui/icons-material';
import { withTranslation } from 'react-i18next';

// Red chip for an invoice that is paid or credited in Verstaani while Amili still has an
// open assignment on the debtor (Invoice.amili_conflict_at). Rendered next to the status
// badge, never instead of it: the status is Amili's view, the conflict is the disagreement.
// `kind` is Invoice.amili_conflict_kind: "paid_on_credited" (customer paid a credited invoice
// to Amili, nobody is collecting) gets its own label; anything else is "still collecting".
const AmiliConflictBadgeBase = ({ t, size = 'small', kind = 'still_collecting' }) => (
  <Chip
    label={t(kind === 'paid_on_credited' ? 'amili.conflictPaidOnCreditedBadge' : 'amili.conflictBadge')}
    size={size}
    icon={<ConflictIcon sx={{ fontSize: size === 'small' ? '14px' : '16px' }} />}
    data-testid="amili-conflict-badge"
    sx={{
      color: '#991b1b',
      bgcolor: '#fee2e2',
      border: '1px solid #fca5a5',
      fontWeight: 600,
      fontSize: size === 'small' ? '11px' : '12px',
      height: size === 'small' ? '22px' : '26px',
      '& .MuiChip-label': { px: 0.75 },
      '& .MuiChip-icon': { color: '#991b1b', marginLeft: '6px' },
    }}
  />
);

export const AmiliConflictBadge = withTranslation()(AmiliConflictBadgeBase);

const statusConfig = {
  pending_sync: {
    color: '#1e40af',
    bgcolor: '#dbeafe',
    borderColor: '#93c5fd',
    icon: PendingSyncIcon,
  },
  not_tracked: {
    color: '#92400e',
    bgcolor: '#fef3c7',
    borderColor: '#fcd34d',
    icon: NotTrackedIcon,
  },
  open: {
    color: '#4b5563',
    bgcolor: '#f3f4f6',
    borderColor: '#d1d5db',
    icon: OpenIcon,
  },
  reminder_sent: {
    color: '#92400e',
    bgcolor: '#fef3c7',
    borderColor: '#fcd34d',
    icon: ReminderIcon,
  },
  in_collection: {
    color: '#991b1b',
    bgcolor: '#fee2e2',
    borderColor: '#fca5a5',
    icon: CollectionIcon,
  },
  paid: {
    color: '#065f46',
    bgcolor: '#d1fae5',
    borderColor: '#6ee7b7',
    icon: PaidIcon,
  },
  partially_paid: {
    color: '#9a3412',
    bgcolor: '#ffedd5',
    borderColor: '#fdba74',
    icon: PartialIcon,
  },
  // Invoice paid in full; Amili is still collecting its own reminder/collection fees from
  // the customer. Green like `paid`, because as far as this invoice goes it is paid.
  principal_paid: {
    color: '#065f46',
    bgcolor: '#d1fae5',
    borderColor: '#6ee7b7',
    icon: PaidIcon,
  },
  // Assignment called off (peruttu). Neutral grey: nothing is being collected any more,
  // which is not the same as a credit loss.
  cancelled: {
    color: '#4b5563',
    bgcolor: '#f3f4f6',
    borderColor: '#d1d5db',
    icon: CancelledIcon,
  },
  credit_loss: {
    color: '#1f2937',
    bgcolor: '#e5e7eb',
    borderColor: '#9ca3af',
    icon: CreditLossIcon,
  },
};

const AmiliPaymentStatusBadge = ({ status, t, size = 'small' }) => {
  if (!status || status === '') {
    return null;
  }

  const config = statusConfig[status] || statusConfig.open;
  const IconComponent = config.icon;

  const getLabel = () => {
    switch (status) {
      case 'pending_sync':
        return t('amili.status.pendingSync', 'Odottaa vahvistusta');
      case 'not_tracked':
        return t('amili.status.notTracked', 'Ei maksuvalvonnassa');
      case 'open':
        return t('amili.status.open', 'Avoin');
      case 'reminder_sent':
        return t('amili.status.reminderSent', 'Muistutus lähetetty');
      case 'in_collection':
        return t('amili.status.inCollection', 'Perinnässä');
      case 'paid':
        return t('amili.status.paid', 'Maksettu');
      case 'partially_paid':
        return t('amili.status.partiallyPaid', 'Osittain maksettu');
      case 'principal_paid':
        return t('amili.status.principalPaid', 'Maksettu, kulut avoinna');
      case 'cancelled':
        return t('amili.status.cancelled', 'Maksuvalvonta peruttu');
      case 'credit_loss':
        return t('amili.status.creditLoss', 'Luottotappio');
      default:
        return status;
    }
  };

  return (
    <Chip
      label={getLabel()}
      size={size}
      icon={<IconComponent sx={{ fontSize: size === 'small' ? '14px' : '16px' }} />}
      sx={{
        color: config.color,
        bgcolor: config.bgcolor,
        border: `1px solid ${config.borderColor}`,
        fontWeight: 500,
        fontSize: size === 'small' ? '11px' : '12px',
        height: size === 'small' ? '22px' : '26px',
        '& .MuiChip-label': {
          px: 0.75,
        },
        '& .MuiChip-icon': {
          color: config.color,
          marginLeft: '6px',
        },
      }}
    />
  );
};

export default withTranslation()(AmiliPaymentStatusBadge);
